import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="geocoder-search"
export default class extends Controller {
  MIN_CHARS_TO_SEARCH = 5;

  static targets = [
    "address",
    "map",
    "mapContainer",
    "latitudeField",
    "longitudeField",
    "postalCode",
    "city",
  ];
  static values = {
    latitude: Number,
    longitude: Number,
    googleApiKey: String,
  };

  connect() {
    this.map = null;
    this.marker = null;
    this.geocoder = null;
    this.dropdownEl = null;
    this.debounceTimer = null;
    this.loadGoogleMaps();
  }

  disconnect() {
    clearTimeout(this.debounceTimer);
    this.dropdownEl?.remove();
  }

  loadGoogleMaps() {
    const apiKey = this.googleApiKeyValue;
    if (!apiKey) return;

    if (window.google?.maps) {
      this.onMapsLoaded();
      return;
    }

    if (document.querySelector("script[data-google-maps]")) {
      window.addEventListener("google-maps-loaded", () => this.onMapsLoaded(), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = "true";
    script.addEventListener("load", () => {
      window.dispatchEvent(new Event("google-maps-loaded"));
      this.onMapsLoaded();
    });
    script.addEventListener("error", () => {
      console.error(
        "[geocoder-search] Failed to load Google Maps script. Check your API key and network connection.",
      );
    });
    document.head.appendChild(script);
  }

  onMapsLoaded() {
    if (this.hasMapTarget) this.initMap();
    if (this.hasAddressTarget) this.initAutocomplete();
  }

  initMap() {
    if (!this.hasMapTarget) return;

    try {
      const defaultCenter = { lat: 48.3, lng: 7.44 }; // Alsace
      const hasCoords = this.latitudeValue && this.longitudeValue;
      const center = hasCoords
        ? { lat: this.latitudeValue, lng: this.longitudeValue }
        : defaultCenter;

      this.map = new google.maps.Map(this.mapTarget, {
        zoom: hasCoords ? 15 : 9,
        center,
        mapId: "dummy",
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      this.geocoder = new google.maps.Geocoder();

      if (hasCoords) this.placeMarker(center);

      if (this.hasMapContainerTarget) {
        this.mapContainerTarget.classList.remove("hidden");
      }
    } catch (error) {
      console.error(
        "[geocoder-search] Failed to initialize Google Maps:",
        error,
      );
    }
  }

  initAutocomplete() {
    const wrapper = this.addressTarget.parentElement;
    wrapper.style.position = "relative";

    this.dropdownEl = document.createElement("ul");
    this.dropdownEl.className =
      "hidden absolute top-full left-0 right-0 z-50 mt-1 py-1 list-none bg-white border border-gray-200 rounded-lg shadow-sm";

    wrapper.appendChild(this.dropdownEl);

    this.addressTarget.addEventListener("input", () => {
      this.clearCoordFields();
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.fetchSuggestions(), 300);
    });

    this.addressTarget.addEventListener("blur", () => {
      // Delay to let mousedown on a suggestion fire first
      setTimeout(() => this.hideDropdown(), 200);
    });

    this.addressTarget.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.hideDropdown();
    });
  }

  async fetchSuggestions() {
    const input = this.addressTarget.value.trim();
    const tooFewChars = input.length < this.MIN_CHARS_TO_SEARCH;
    if (!input || tooFewChars) {
      this.hideDropdown();
      return;
    }

    try {
      const { AutocompleteSuggestion } =
        await google.maps.importLibrary("places");
      const { suggestions } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions({ input });

      if (suggestions?.length) {
        this.renderSuggestions(suggestions);
      } else {
        this.hideDropdown();
        // Still attempt to geocode for map preview
        this.geocodeAddress(input);
      }
    } catch (error) {
      console.error("[geocoder-search] Failed to fetch suggestions:", error);
      this.hideDropdown();
    }
  }

  renderSuggestions(suggestions) {
    this.dropdownEl.innerHTML = "";
    const computed = window.getComputedStyle(this.addressTarget);

    suggestions.slice(0, 5).forEach((suggestion) => {
      const prediction = suggestion.placePrediction;
      const li = document.createElement("li");
      li.className = "px-3 py-2 cursor-pointer leading-[1.4] hover:bg-gray-50";
      li.style.fontSize = computed.fontSize;
      li.style.fontFamily = computed.fontFamily;
      li.style.color = computed.color;

      const main = document.createElement("span");
      main.textContent = prediction.mainText?.text ?? prediction.text.text;

      const secondary = prediction.secondaryText?.text;
      if (secondary) {
        const sub = document.createElement("span");
        sub.className = "text-gray-500 text-sm";
        sub.textContent = ` ${secondary}`;
        li.append(main, sub);
      } else {
        li.appendChild(main);
      }
      li.addEventListener("mousedown", (e) => {
        e.preventDefault(); // Prevent blur before click
        this.selectSuggestion(prediction);
      });

      this.dropdownEl.appendChild(li);
    });

    this.showDropdown();
  }

  async selectSuggestion(prediction) {
    this.hideDropdown();

    const wantsComponents = this.hasPostalCodeTarget || this.hasCityTarget;
    if (!wantsComponents) this.addressTarget.value = prediction.text.text;

    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: wantsComponents
          ? ["location", "addressComponents"]
          : ["location"],
      });

      if (wantsComponents) this.fillAddressFields(place, prediction);

      if (place.location) {
        clearTimeout(this.debounceTimer);
        if (this.hasMapTarget) {
          this.updateMap(place.location);
        } else {
          this.setCoordFields(place.location.lat(), place.location.lng());
        }
      }
    } catch (error) {
      console.error("[geocoder-search] Failed to fetch place location:", error);
      this.geocodeAddress(this.addressTarget.value);
    }
  }

  fillAddressFields(place, prediction) {
    const components = place.addressComponents ?? [];
    const pick = (type) =>
      components.find((c) => c.types.includes(type))?.longText ?? "";

    const line1 = [pick("street_number"), pick("route")]
      .filter(Boolean)
      .join(" ");
    this.addressTarget.value = line1 || prediction.text.text;

    if (this.hasPostalCodeTarget) {
      const postalCode = pick("postal_code");
      if (postalCode) this.postalCodeTarget.value = postalCode;
    }

    if (this.hasCityTarget) {
      const city =
        pick("locality") ||
        pick("postal_town") ||
        pick("administrative_area_level_2");
      if (city) this.cityTarget.value = city;
    }
  }

  geocodeAddress(address) {
    if (!address || !this.geocoder || !this.map) return;

    this.geocoder.geocode({ address }, (results, status) => {
      if (status === "OK" && results.length > 0) {
        this.updateMap(results[0].geometry.location);
      }
    });
  }

  showDropdown() {
    this.dropdownEl.style.display = "block";
  }

  hideDropdown() {
    this.dropdownEl.style.display = "none";
  }

  updateMap(location) {
    this.map.setCenter(location);
    this.map.setZoom(15);
    this.placeMarker(location);
    this.setCoordFields(location.lat(), location.lng());
  }

  setCoordFields(latitude, longitude) {
    if (this.hasLatitudeFieldTarget) {
      this.latitudeFieldTarget.value = latitude;
    }
    if (this.hasLongitudeFieldTarget) {
      this.longitudeFieldTarget.value = longitude;
    }
  }

  clearCoordFields() {
    if (this.hasLatitudeFieldTarget) this.latitudeFieldTarget.value = "";
    if (this.hasLongitudeFieldTarget) this.longitudeFieldTarget.value = "";
  }

  placeMarker(position) {
    if (this.marker) {
      this.marker.position = position;
    } else {
      this.marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position,
      });
    }
  }
}
